import { AnimatePresence, motion } from "framer-motion";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";

import { useClipboard } from "../../../hooks/useClipboard";
import { ActivePulseIndicator } from "../../common/ActivePulseIndicator";
import { ChevronRightIcon } from "../../common/icons";
import { Modal } from "../../common/Modal";
import {
  type ContextBlock,
  getPhasePrompts,
  getRawUserContextBlocks,
  getRunStartEvent,
  getSystemContextBlocks,
  getUserContextBlocks,
  readPhaseValue,
  readString,
  readStringArray,
  stripSystemContextFromPrompt,
} from "./promptData";
import { type RunLifecycleStatus, getRunLifecycleStatus } from "./runLifecycle";

interface PromptInspectorModalProps {
  events: ProviderProgressEntry[];
  onClose: () => void;
  streamLabel: string;
  initialTabId?: string;
  layoutId?: string;
}

interface InspectorTabDescriptor {
  count?: number;
  icon: string;
  id: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Rough token estimate (~4 chars per token for English text). */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/** Format token count for display. */
function formatTokenCount(tokens: number): string {
  if (tokens < 1000) {
    return `~${tokens}`;
  }
  if (tokens < 1_000_000) {
    return `~${(tokens / 1000).toFixed(1)}k`;
  }
  return `~${(tokens / 1_000_000).toFixed(2)}M`;
}

/** Escape regex special characters for use in RegExp constructor. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function getRunStatusLabel(runStatus: RunLifecycleStatus): string {
  switch (runStatus) {
    case "active": {
      return "Live Stream Active";
    }
    case "ended": {
      return "Stream Complete";
    }
    case "error": {
      return "Stream Error";
    }
    case "idle": {
      return "Stream Inactive";
    }
  }

  const exhaustiveRunStatus: never = runStatus;
  void exhaustiveRunStatus;
  throw new Error("Unhandled run status");
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Renders text with search matches highlighted. */
const HighlightedText: FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  if (!query) {
    return <>{text}</>;
  }

  const regex = new RegExp(`(${escapeRegex(query)})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            className="bg-amber-400/30 text-amber-200 rounded-none px-px"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

/** Counts occurrences of a query in text (case-insensitive). */
function countMatches(text: string, query: string): number {
  if (!query) {
    return 0;
  }
  const regex = new RegExp(escapeRegex(query), "gi");
  return (text.match(regex) ?? []).length;
}

/** Inline search bar for content panes. */
const PaneSearchBar: FC<{
  query: string;
  onChange: (value: string) => void;
  matchCount: number;
}> = ({ query, onChange, matchCount }) => (
  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
    <div className="relative flex min-w-0 flex-1 items-center sm:w-auto sm:flex-none">
      <svg
        className="absolute left-2 h-3 w-3 text-slate-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="square"
          strokeLinejoin="miter"
          strokeWidth="2.5"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Find..."
        data-inspector-search
        className="h-8 w-full min-w-0 border border-slate-600 bg-arti-background pl-7 pr-2 font-mono text-xs text-slate-300 placeholder:text-slate-600 transition-colors focus:border-emerald-500 focus:outline-none sm:h-6 sm:w-32 sm:text-[11px] md:w-40 lg:w-48"
      />
    </div>
    {query && (
      <span
        aria-live="polite"
        className="font-mono text-[10px] font-bold text-slate-500 sm:whitespace-nowrap"
      >
        {matchCount} {matchCount === 1 ? "match" : "matches"}
      </span>
    )}
  </div>
);

/** Token count badge. */
const TokenBadge: FC<{ text: string }> = ({ text }) => {
  const tokens = estimateTokens(text);
  return (
    <span
      className="border border-slate-600 bg-slate-800 px-2 py-1 font-mono text-[10px] font-bold text-slate-400 sm:px-1.5 sm:py-0.5 sm:text-[9px]"
      title={`Estimated ~${tokens.toLocaleString()} tokens (${text.length.toLocaleString()} chars)`}
    >
      {formatTokenCount(tokens)} tok
    </span>
  );
};

/** Copy button with icon toggle (copy → check). */
const InlineCopyButton: FC<{ text: string }> = ({ text }) => {
  const { status, copy } = useClipboard();

  return (
    <button
      type="button"
      onClick={() => void copy(text)}
      className="flex h-8 w-8 shrink-0 items-center justify-center border border-slate-600 bg-slate-800 text-slate-400 transition-all hover:border-emerald-500 hover:text-emerald-400 focus:outline-none sm:h-6 sm:w-6"
      title="Copy to clipboard"
      aria-label="Copy to clipboard"
    >
      {status === "copied" ? (
        <svg
          className="h-3 w-3 text-emerald-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="square"
            strokeLinejoin="miter"
            strokeWidth="2.5"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="square"
            strokeLinejoin="miter"
            strokeWidth="2"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
};

/** Pane toolbar with icon, title, token count, search, and copy. */
const PaneToolbar: FC<{
  icon: ReactNode;
  title: string;
  titleClassName?: string;
  text: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}> = ({ icon, title, titleClassName, text, searchQuery, onSearchChange }) => {
  const matchCount = countMatches(text, searchQuery);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-700 bg-slate-800/50 px-3 py-3 sm:px-4 sm:py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="shrink-0">{icon}</span>
        <span
          className={`min-w-0 truncate font-mono text-[11px] font-bold uppercase tracking-[0.18em] sm:text-xs ${titleClassName ?? "text-slate-300"}`}
        >
          {title}
        </span>
      </div>
      <div className="ml-auto flex items-center gap-2 sm:ml-0">
        <TokenBadge text={text} />
        <InlineCopyButton text={text} />
      </div>
      <div className="basis-full sm:ml-auto sm:basis-auto">
        <PaneSearchBar
          query={searchQuery}
          onChange={onSearchChange}
          matchCount={matchCount}
        />
      </div>
    </div>
  );
};

/** Collapsible context block with expand/collapse. */
const CollapsibleBlock: FC<{
  index: number;
  block: ContextBlock;
  searchQuery: string;
  defaultOpen?: boolean;
}> = ({ index, block, searchQuery, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-700 bg-arti-background transition-colors duration-200 hover:border-emerald-500/30">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-start gap-2 px-3 py-3 text-left transition-colors hover:bg-slate-800/30 sm:items-center sm:px-4 sm:py-2.5"
      >
        <ChevronRightIcon
          size={12}
          className={`mt-0.5 h-3 w-3 shrink-0 text-slate-500 transition-transform duration-200 sm:mt-0 ${isOpen ? "rotate-90" : ""}`}
        />
        <span className="bg-emerald-500/20 px-1.5 py-0.5 font-mono text-[10px] font-bold text-emerald-400">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h4 className="min-w-0 flex-1 truncate pt-0.5 text-sm font-bold uppercase tracking-tight text-slate-200 sm:pt-0">
          {block.title}
        </h4>
        <div className="flex shrink-0 items-center gap-2">
          <TokenBadge text={block.body} />
          <InlineCopyButton text={block.body} />
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-slate-700/50 px-3 pb-3 pt-3 sm:px-4 sm:pb-4">
          <pre className="whitespace-pre-wrap break-words bg-arti-panel p-2.5 font-mono text-xs text-slate-300 sm:p-3">
            <HighlightedText text={block.body} query={searchQuery} />
          </pre>
        </div>
      )}
    </div>
  );
};

const InspectorTabButton: FC<{
  isActive: boolean;
  onClick: () => void;
  tab: InspectorTabDescriptor;
}> = ({ isActive, onClick, tab }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex shrink-0 items-center gap-2 border-r border-slate-200 px-3 py-2.5 transition-all duration-200 sm:px-6 sm:py-3 dark:border-slate-700 ${
      isActive
        ? "bg-white text-emerald-600 dark:bg-arti-background dark:text-emerald-400"
        : "text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300"
    }`}
  >
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeWidth="2"
        d={tab.icon}
      />
    </svg>
    <span className="font-mono text-[10px] font-bold tracking-[0.18em] sm:text-[11px]">
      {tab.label}
    </span>
    {tab.count !== undefined ? (
      <span
        className={`rounded-sm px-1 text-[9px] font-black ${isActive ? "bg-emerald-500 text-white dark:text-arti-background" : "bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-400"}`}
      >
        {tab.count}
      </span>
    ) : null}
    {isActive ? (
      <motion.div
        layoutId="active-tab-underline"
        className="absolute left-0 right-0 top-0 h-0.5 bg-emerald-500"
      />
    ) : null}
  </button>
);

const PhaseTimelineItem: FC<{
  isActive: boolean;
  isCurrent: boolean;
  isVisited: boolean;
  onClick: () => void;
  phase: string;
}> = ({ isActive, isCurrent, isVisited, onClick, phase }) => {
  const phaseLabel = phase.replace(/_/g, " ");
  let mobileButtonClassName =
    "cursor-not-allowed border-dashed border-slate-300/70 bg-transparent text-slate-400 opacity-60 dark:border-slate-700/60 dark:text-slate-600";

  if (isActive) {
    mobileButtonClassName =
      "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
  } else if (isCurrent) {
    mobileButtonClassName =
      "border-slate-400 bg-white text-slate-900 dark:border-slate-600 dark:bg-arti-background dark:text-slate-200";
  } else if (isVisited) {
    mobileButtonClassName =
      "border-slate-300 bg-white text-slate-600 hover:border-emerald-500/40 hover:text-slate-900 dark:border-slate-700 dark:bg-arti-background dark:text-slate-400 dark:hover:text-slate-200";
  }

  let nodeColorClass =
    "bg-white border border-slate-300 dark:bg-slate-800 dark:border-slate-700";
  if (isActive) {
    nodeColorClass =
      "bg-emerald-500 scale-125 shadow-[0_0_8px_rgba(34,197,94,0.6)]";
  } else if (isCurrent) {
    nodeColorClass =
      "bg-emerald-500 border-2 border-emerald-900 dark:bg-emerald-400 dark:border-emerald-900";
  } else if (isVisited) {
    nodeColorClass = "bg-slate-400 dark:bg-slate-500";
  }

  let textColorClass = "text-slate-400 cursor-not-allowed";
  if (isActive) {
    textColorClass =
      "text-emerald-600 bg-emerald-500/5 dark:text-emerald-400 dark:bg-emerald-500/5";
  } else if (isCurrent) {
    textColorClass = "text-slate-900 dark:text-slate-200";
  } else if (isVisited) {
    textColorClass =
      "text-slate-600 hover:text-slate-900 hover:bg-slate-200 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/50";
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={!isVisited}
        className={`inline-flex min-w-max items-center gap-2 border px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-all duration-200 lg:hidden ${mobileButtonClassName}`}
      >
        <span
          className={`h-2 w-2 shrink-0 transition-all duration-300 ${nodeColorClass}`}
        />
        <span>{phaseLabel}</span>
      </button>
      <div className="group relative hidden items-center lg:flex">
        <div className="relative z-10 flex w-7 justify-center">
          <div
            className={`h-2 w-2 transition-all duration-300 ${nodeColorClass}`}
          />
        </div>
        <button
          type="button"
          onClick={onClick}
          disabled={!isVisited}
          className={`flex-1 px-3 py-1.5 text-left font-mono text-[11px] font-bold uppercase transition-all duration-200 ${textColorClass}`}
        >
          {phaseLabel}
          {isActive ? (
            <motion.div
              layoutId="sidebar-active"
              className="pointer-events-none absolute inset-0 border-l-2 border-emerald-500"
            />
          ) : null}
        </button>
      </div>
    </>
  );
};

const LastPhaseTabButton: FC<{
  isActive: boolean;
  onClick: () => void;
  phaseTabId: string;
}> = ({ isActive, onClick, phaseTabId }) => (
  <motion.button
    key={phaseTabId}
    type="button"
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -10 }}
    onClick={onClick}
    className={`relative flex shrink-0 items-center gap-2 border-r border-slate-200 px-3 py-2.5 transition-all duration-200 sm:px-6 sm:py-3 dark:border-slate-700 ${
      isActive
        ? "bg-white text-emerald-600 dark:bg-arti-background dark:text-emerald-400"
        : "text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300"
    }`}
  >
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeWidth="2"
        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
      />
    </svg>
    <span className="font-mono text-[10px] font-bold tracking-[0.18em] sm:text-[11px]">
      PHASE::{phaseTabId.replace("phase-", "").toUpperCase()}
    </span>
    {isActive ? (
      <motion.div
        layoutId="active-tab-underline"
        className="absolute left-0 right-0 top-0 h-0.5 bg-emerald-500"
      />
    ) : null}
  </motion.button>
);

const RunStatusIndicator: FC<{
  className?: string;
  isActive: boolean;
  label: string;
}> = ({ className, isActive, label }) => {
  const dotClassName = isActive ? "bg-emerald-500/50" : "bg-slate-400/70";

  return (
    <div
      className={`flex items-center gap-2 text-slate-400 dark:text-slate-500 ${className ?? ""}`}
    >
      <div className={`h-2 w-2 ${dotClassName}`} />
      <span className="font-mono text-[9px] font-bold uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const PromptInspectorModal: FC<PromptInspectorModalProps> = ({
  events,
  onClose,
  streamLabel,
  initialTabId,
  layoutId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Reset search when switching tabs
  const [activeTab, setActiveTab] = useState<string>(
    initialTabId || "user-prompt",
  );

  // Track the last-visited phase tab so it stays in the tab bar
  const [lastPhaseTab, setLastPhaseTab] = useState<string | null>(
    initialTabId?.startsWith("phase-") ? initialTabId : null,
  );

  const switchTab = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setSearchQuery("");
    if (tabId.startsWith("phase-")) {
      setLastPhaseTab(tabId);
    }
  }, []);

  // Extract prompts and context blocks
  const runStartEvent = useMemo(() => getRunStartEvent(events), [events]);
  const runStatus = useMemo(() => getRunLifecycleStatus(events), [events]);
  const runIsActive = runStatus === "active";

  const { phases, visitedPhases, currentPhase } = useMemo(() => {
    const modePhases = readStringArray(runStartEvent?.event_data.phases);
    const visited = new Set<string>();
    let current: string | null = null;
    for (const e of events) {
      if (
        e.event_type === "phase:system-prompt" ||
        e.event_type === "phase:status"
      ) {
        const p = readPhaseValue(e);
        if (p) {
          visited.add(p);
          current = p;
        }
      }
    }
    return {
      currentPhase: current,
      phases: modePhases,
      visitedPhases: visited,
    };
  }, [events, runStartEvent]);

  const initialUserPrompt = useMemo(
    () => readString(runStartEvent?.event_data.prompt),
    [runStartEvent],
  );

  const systemContextBlocks = useMemo(
    () => getSystemContextBlocks(runStartEvent),
    [runStartEvent],
  );

  const rawUserContextBlocks = useMemo(
    () => getRawUserContextBlocks(runStartEvent),
    [runStartEvent],
  );

  const userContextBlocks = useMemo(
    () => getUserContextBlocks(runStartEvent),
    [runStartEvent],
  );

  const phasePrompts = useMemo(() => getPhasePrompts(events), [events]);

  // Adjust active tab if it's no longer valid
  useEffect(() => {
    const isValid =
      activeTab === "user-prompt" ||
      activeTab === "user-context" ||
      activeTab === "system-context" ||
      activeTab.startsWith("phase-");
    if (!isValid) {
      setActiveTab("user-prompt");
    }
  }, [activeTab]);

  // Keyboard shortcut: Ctrl/Cmd+F to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        // Focus the search input in the pane toolbar
        const input = document.querySelector<HTMLInputElement>(
          "[data-inspector-search]",
        );
        input?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const renderContextBlocksPane = (
    blocks: ContextBlock[],
    type: "user" | "system",
  ) => {
    const fullText = blocks.map((b) => `${b.title}\n${b.body}`).join("\n\n");

    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <PaneToolbar
          icon={
            <svg
              className="h-4 w-4 text-emerald-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          }
          title={`${type} Context Blocks`}
          text={fullText}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <div className="custom-scrollbar flex-1 space-y-2 overflow-auto p-3 sm:p-4">
          {blocks.map((block, idx) => (
            <CollapsibleBlock
              key={idx}
              index={idx}
              block={block}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      </div>
    );
  };

  // Determine the phase currently being inspected
  const inspectedPhaseName = activeTab.startsWith("phase-")
    ? activeTab.replace("phase-", "")
    : (lastPhaseTab?.replace("phase-", "") ?? phasePrompts[0]?.phase);
  const livePhase = runIsActive ? currentPhase : null;
  const inspectedPhaseIsLive =
    runIsActive &&
    inspectedPhaseName != null &&
    inspectedPhaseName === livePhase;
  const runStatusLabel = getRunStatusLabel(runStatus);

  const renderContent = () => {
    if (activeTab === "user-prompt") {
      const unbuiltPrompt = readString(runStartEvent?.event_data.unbuiltPrompt);

      // Resolve the system prompt for the currently inspected phase.
      const currentPhasePrompt = inspectedPhaseName
        ? phasePrompts.find((p) => p.phase === inspectedPhaseName)
        : phasePrompts[0];

      const cleanPhaseSystemPrompt = currentPhasePrompt?.systemPrompt
        ? stripSystemContextFromPrompt(
            currentPhasePrompt.systemPrompt,
            systemContextBlocks,
          )
        : null;

      const sections = [
        {
          blocks: cleanPhaseSystemPrompt
            ? [{ body: cleanPhaseSystemPrompt, title: "System Instructions" }]
            : [],
          title: "Phase System",
        },
        { blocks: systemContextBlocks, title: "System Context" },
        { blocks: rawUserContextBlocks, title: "User Context" },
        {
          blocks: unbuiltPrompt
            ? [{ body: unbuiltPrompt, title: "Instruction" }]
            : [],
          title: "User Prompt",
        },
      ].filter((s) => s.blocks.length > 0);

      if (sections.length > 0) {
        const fullText = sections
          .map(
            (s) =>
              `# ${s.title.toUpperCase()}\n\n${s.blocks.map((b) => `${b.title}\n${b.body}`).join("\n\n")}`,
          )
          .join("\n\n---\n\n");

        return (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <PaneToolbar
              icon={
                <svg
                  className="h-4 w-4 text-emerald-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth="2"
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              }
              title="Full Prompt Construction"
              text={fullText}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <div className="custom-scrollbar flex-1 space-y-6 overflow-auto p-3 sm:space-y-8 sm:p-4">
              {sections.map((section, sIdx) => (
                <div key={sIdx} className="space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {section.title}
                    </h3>
                    <div className="h-px flex-1 bg-slate-800" />
                  </div>
                  <div className="space-y-2">
                    {section.blocks.map((block, bIdx) => (
                      <CollapsibleBlock
                        key={bIdx}
                        index={bIdx}
                        block={block}
                        searchQuery={searchQuery}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      const text = initialUserPrompt ?? "No user prompt provided.";
      return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <PaneToolbar
            icon={
              <svg
                className="h-4 w-4 text-emerald-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                  strokeWidth="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            }
            title="Initial User Prompt"
            text={text}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <pre className="custom-scrollbar flex-1 overflow-auto whitespace-pre-wrap break-words bg-arti-background p-4 font-mono text-xs text-slate-300 selection:bg-emerald-500/30 sm:p-6 sm:text-sm">
            <HighlightedText text={text} query={searchQuery} />
          </pre>
        </div>
      );
    }

    if (activeTab === "user-context") {
      return renderContextBlocksPane(userContextBlocks, "user");
    }
    if (activeTab === "system-context") {
      return renderContextBlocksPane(systemContextBlocks, "system");
    }

    const phase = activeTab.replace("phase-", "");
    const phasePrompt = phasePrompts.find((p) => p.phase === phase);
    const text =
      phasePrompt?.systemPrompt ?? "No system prompt found for this phase.";
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <PaneToolbar
          icon={
            inspectedPhaseIsLive ? (
              <ActivePulseIndicator size="sm" />
            ) : (
              <div className="h-2 w-2 bg-slate-500" />
            )
          }
          title={`${phase.replace(/_/g, " ")} :: System Prompt`}
          titleClassName={
            inspectedPhaseIsLive ? "text-emerald-400" : "text-slate-300"
          }
          text={text}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <pre className="custom-scrollbar flex-1 overflow-auto whitespace-pre-wrap break-words bg-arti-background p-4 font-mono text-xs text-slate-300 selection:bg-emerald-500/30 sm:p-6 sm:text-sm">
          <HighlightedText text={text} query={searchQuery} />
        </pre>
      </div>
    );
  };

  const inspectorTabs: InspectorTabDescriptor[] = [
    {
      icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
      id: "user-prompt",
      label: "PROMPT",
    },
    {
      count: userContextBlocks.length,
      icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
      id: "user-context",
      label: "USER_CTX",
    },
    {
      count: systemContextBlocks.length,
      icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
      id: "system-context",
      label: "SYSTEM_CTX",
    },
  ];

  const phaseTimelineItems = phases.map((phase) => (
    <PhaseTimelineItem
      key={phase}
      isActive={phase === inspectedPhaseName}
      isCurrent={phase === livePhase}
      isVisited={visitedPhases.has(phase)}
      onClick={() => {
        if (visitedPhases.has(phase)) {
          switchTab(`phase-${phase}`);
        }
      }}
      phase={phase}
    />
  ));
  const lastPhaseTabButton = lastPhaseTab ? (
    <LastPhaseTabButton
      isActive={activeTab === lastPhaseTab}
      onClick={() => switchTab(lastPhaseTab)}
      phaseTabId={lastPhaseTab}
    />
  ) : null;

  return (
    <Modal
      title="AGENT_INSPECTOR"
      subtitle={`[ID::${streamLabel.toUpperCase()}]`}
      onClose={onClose}
      useMotion
      widthClass="max-w-6xl"
      bodyClassName="flex h-[100dvh] flex-col overflow-hidden bg-white p-0 text-slate-900 dark:bg-arti-background dark:text-slate-200 md:h-[85vh]"
      overlayMotionProps={{
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        initial: { opacity: 0 },
        transition: { duration: 0.2 },
      }}
      panelMotionProps={
        layoutId
          ? {
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              initial: { opacity: 0, scale: 0.95 },
              layoutId,
              transition: { layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
            }
          : {
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              initial: { opacity: 0, scale: 0.95 },
              transition: { duration: 0.2 },
            }
      }
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        {/* Left Sidebar: Phase Timeline */}
        <div className="flex w-full shrink-0 flex-col border-b border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-arti-panel lg:w-64 lg:border-b-0 lg:border-r">
          <div className="border-b border-slate-200 px-3 py-3 dark:border-slate-700 sm:px-4 lg:py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Execution Timeline
              </h3>
              <RunStatusIndicator
                className="lg:hidden"
                isActive={runIsActive}
                label={runStatusLabel}
              />
            </div>

            <div className="custom-scrollbar mt-3 overflow-x-auto lg:hidden">
              <div className="flex w-max items-center gap-2 pr-3">
                {phaseTimelineItems}
              </div>
            </div>

            <div className="relative mt-4 hidden space-y-1 lg:block">
              {/* Rail */}
              <div className="absolute bottom-2 left-3.5 top-2 w-px bg-slate-300 dark:bg-slate-700" />

              <div className="space-y-3">{phaseTimelineItems}</div>
            </div>
          </div>

          <div className="mt-auto hidden border-t border-slate-200 p-4 dark:border-slate-700 lg:block">
            <RunStatusIndicator isActive={runIsActive} label={runStatusLabel} />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-arti-background">
          {/* Top Integrated Tabs */}
          <div className="custom-scrollbar overflow-x-auto border-b border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-arti-panel">
            <div className="flex min-w-full items-stretch">
              {inspectorTabs.map((tab) => {
                if (tab.count !== undefined && tab.count === 0) {
                  return null;
                }

                return (
                  <InspectorTabButton
                    key={tab.id}
                    isActive={activeTab === tab.id}
                    onClick={() => switchTab(tab.id)}
                    tab={tab}
                  />
                );
              })}

              {/* Dynamic Phase Tab — persists after visiting a phase */}
              <AnimatePresence>{lastPhaseTabButton}</AnimatePresence>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            {renderContent()}
          </div>
        </div>
      </div>
    </Modal>
  );
};
